import clsx from "clsx";
import React from "react";
import { Box, DialogProps, TextField, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ActionButton } from "../button/ActionButton";
import { BaseDialog } from "./BaseDialog";
import { useCurrentBrand } from "../../hooks/useCurrentWorkspace";
import LoadingActionButton from "../button/LoadingActionButton";
import {
  ActiveClientThresholdAlert,
  AlertDisplayMode,
} from "../coach-clients/ActiveClientThresholdAlert";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import {
  getClientExceededAlertTitle,
  getClientExceededThresholdAlertText,
  getNextHigherPlan,
} from "../../utils/user";
import { CLIENTS_ALERT_THRESHOLD, Plan, planClients } from "../../constants";
import { getDefaultInviteMessage } from "./InviteDialog";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    maxWidth: 650,
    width: 650,
  },

  title: {
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    cursor: "pointer",
  },

  text: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "17px",
    color: theme.palette.text.primary,
  },

  messageInput: {
    cursor: "text",
    height: 240,

    "& > textarea": {
      height: "100% !important",
    },
  },

  buttons: {
    display: "flex",
    marginTop: theme.spacing(3),
  },

  cancelButton: {
    fontSize: 16,
    fontWeight: "bold",
    color: theme.palette.text.secondary,
    borderRadius: 4,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: theme.palette.text.secondary,
    marginRight: theme.spacing(3),
    width: "30%",
    height: theme.spacing(7),
  },

  inviteButton: {
    flexGrow: 1,
    height: theme.spacing(7),
  },

  addButton: {
    margin: theme.spacing(0, 3, 0, 0),
  },

  alertContainer: {
    marginBottom: theme.spacing(2),
  },
}));

export interface SendEmailDialogProps extends DialogProps {
  sendingEmail: boolean;
  sendEmail: (message: string) => void;
}

export function SendEmailDialog(props: SendEmailDialogProps) {
  const { className, onClose, sendEmail, sendingEmail, ...other } = props;
  const s = useStyles();
  const { isBrandedWorkspace, brandName } = useCurrentBrand();
  const currentUser = useCurrentUser();

  const nextPlan = getNextHigherPlan(currentUser.plan as Plan);
  const currentPlanLimit = Number(planClients(currentUser.plan as Plan, true));
  const planNumber = Number(planClients(nextPlan, true));

  const DEFAULT_MESSAGE = getDefaultInviteMessage(brandName);

  const [message, setMessage] = React.useState<string>(DEFAULT_MESSAGE);

  const handleMessageChange = React.useCallback(({ target: { value } }) => {
    setMessage(value);
  }, []);

  const handleCancel = React.useCallback(() => {
    onClose(undefined, undefined);
  }, [onClose]);

  const disabled = sendingEmail;

  return (
    <>
      <BaseDialog
        className={clsx(s.root, className)}
        title={"Invite by email"}
        classes={{
          paper: s.paper,
        }}
        onClose={onClose}
        {...other}
      >
        <Box className={s.alertContainer}>
          {currentPlanLimit - currentUser.clientsCountNoSample <=
            CLIENTS_ALERT_THRESHOLD && (
            <ActiveClientThresholdAlert
              alertTitle={getClientExceededAlertTitle(
                isBrandedWorkspace,
                brandName,
              )}
              mode={AlertDisplayMode.COLLAPSE}
              fullText={getClientExceededThresholdAlertText(planNumber)}
            />
          )}
        </Box>
        <Box>
          <TextField
            fullWidth
            multiline
            value={message}
            onChange={handleMessageChange}
            slotProps={{
              input: {
                disableUnderline: true,
                className: s.messageInput,
              },
            }}
          />
          <Box className={s.buttons}>
            <Button
              className={s.cancelButton}
              variant="outlined"
              children="Cancel"
              onClick={handleCancel}
              disabled={disabled}
            />
            <LoadingActionButton
              className={clsx(s.inviteButton, s.addButton)}
              children="Send Invite"
              onClick={() => sendEmail(message)}
              disabled={disabled}
              loading={sendingEmail}
            />
          </Box>
        </Box>
      </BaseDialog>
    </>
  );
}
